/**
 * Pipelines API Schemas
 *
 * Zod schemas for pipeline workflow start endpoint.
 * Used by workflows-api POST /api/v1/pipelines/start
 *
 * SINGLE SOURCE OF TRUTH for pipeline input contract.
 */

import { z } from 'zod/v4';

// ============================================================================
// Enums
// ============================================================================

/**
 * Communication channel type for the record
 * - 'call': Voice/audio recording (requires audioUrl)
 * - 'chat': Text-based conversation (requires transcript)
 */
export const pipelineChannelSchema = z.enum(['call', 'chat']);

export const replayModeSchema = z.enum(['analysis_only', 'from_asr_source']);
export const analysisPublicationSchema = z.enum(['shadow', 'promote']);

/**
 * Internal, already-authorized replay contract. The edge API resolves and
 * tenant-checks sourceTranscriptId before this payload crosses the service
 * boundary; workflows never accept caller-supplied transcript text for replay.
 */
export const pipelineReplaySchema = z
  .object({
    /** Stable operation ID: workflow creation and paid-cost keys dedupe on it. */
    runId: z.string().uuid(),
    mode: replayModeSchema,
    sourceTranscriptId: z.string().uuid(),
    /** Exact analysis supplying downstream-only context; absent for ASR replay. */
    sourceAnalysisId: z.string().uuid().nullable(),
    publish: analysisPublicationSchema,
    expectedCurrentAnalysisId: z.string().uuid().nullable(),
    // Historical replay never performs external delivery. A future reviewed
    // delivery flow gets its own approval command instead of weakening this.
    deliverToCrm: z.literal(false),
    /**
     * Who asked for the replay, when it was NOT the customer: `bulk_manifest`
     * — an operator's approved bulk manifest (`replay-analysis-manifest`,
     * marked by the request's `authorization`). Absent — the customer's own
     * re-analyse action. Steps the legal texts promise only for a
     * conversation the customer re-analyses (Voice of Customer extraction,
     * #843) skip a bulk replay.
     *
     * Optional and set ONLY on the bulk path: the schema is strict, so a
     * field every replay carried would be rejected by a workflows-api that
     * predates it while edge-api deploys first — and customer replays would
     * fail in that window. Only an operator's manifest run can meet the skew.
     */
    origin: z.literal('bulk_manifest').optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.mode === 'analysis_only' && !data.sourceAnalysisId) {
      ctx.addIssue({
        code: 'custom',
        path: ['sourceAnalysisId'],
        message: 'analysis_only replay requires an exact source analysis',
      });
    }
    if (data.mode === 'from_asr_source' && data.sourceAnalysisId) {
      ctx.addIssue({
        code: 'custom',
        path: ['sourceAnalysisId'],
        message: 'from_asr_source replay must rebuild analysis context',
      });
    }
  });

// ============================================================================
// Trace ID
// ============================================================================

/**
 * Trace ID: 16-32 hex chars (CF-Ray or OpenTelemetry format).
 *
 * Mirrors TRACE_ID_PATTERN in @aica/observability — kept as a literal here
 * because api-types is bundled into the browser and must not pull in the
 * tracing runtime. The workflow's own input schema enforces the same shape, so
 * a looser rule here only defers the rejection until after the workflow has
 * been created (record left stranded). Notably `crypto.randomUUID()` does NOT
 * match: hyphens.
 */
export const traceIdSchema = z
  .string()
  .regex(
    /^[0-9a-f]{16,32}$/i,
    'Trace ID must be a 16-32 character hexadecimal string (CF-Ray or OpenTelemetry format)'
  );

// ============================================================================
// Diarization Schema (Speaker turns)
// ============================================================================

/**
 * Speaker diarization segment
 *
 * Represents a single speaker turn with timing and text.
 * Used when ASR provider returns speaker separation.
 *
 * @property speaker - Speaker identifier (e.g., "speaker_0", "agent", "customer")
 * @property role - Optional machine-readable speaker role (agent/customer/system/unknown)
 * @property start - Start time in seconds
 * @property end - End time in seconds
 * @property text - Transcript text for this speaker segment
 * @property confidence - Optional confidence score (0-1) from ASR provider
 */
export const diarizationSegmentSchema = z.object({
  speaker: z.string(),
  // Mirrors ChatRoleSchema in packages/core/src/schemas/chat.ts. Duplicated
  // (not imported) because @aica/api-types does not depend on @aica/core.
  // Drift from ChatRoleSchema fails packages/core/src/schemas/chat.test.ts.
  role: z.enum(['agent', 'customer', 'system', 'unknown']).optional(),
  start: z.number(),
  end: z.number(),
  text: z.string(),
  confidence: z.number().optional(),
  // Chat: an employee message — kept or dropped as empty after markup
  // stripping — precedes this one (`ChatDiarizationSegment.afterAgentMessage`
  // in @aica/core, #1578). Without it Zod would strip the first-minute
  // boundary of a manually uploaded chat on its way to the workflow.
  afterAgentMessage: z.literal(true).optional(),
  // Chat: a message of an unknown-role author, dropped as empty, precedes
  // this one before the first employee answer — the boundary is unknown
  // (`ChatDiarizationSegment.afterUnknownMessage`, #1578).
  afterUnknownMessage: z.literal(true).optional(),
});

// ============================================================================
// Pipeline Start Request
// ============================================================================

/**
 * Request body for POST /api/v1/pipelines/start
 *
 * Starts a new RecordPipelineWorkflow instance.
 * One of audioUrl, transcript, or awaitMedia must be provided.
 *
 * @property recordId - UUID of the record to process
 * @property projectId - UUID of the project this record belongs to
 * @property orgId - UUID of the organization
 * @property audioUrl - Optional URL to audio file (required for 'call' channel)
 * @property transcript - Optional pre-existing transcript text (required for 'chat' channel)
 * @property diarization - Optional speaker diarization data (array of speaker turns)
 * @property channel - Communication channel type ('call' or 'chat')
 * @property traceId - Optional trace ID for distributed tracing
 * @property sourceLanguage - Optional original/source language hint from ingress
 * @property promptProfileId - Optional prompt profile override for re-analysis
 * @property forceReanalyze - Skip idempotency check and force new analysis (for re-analyze)
 * @property awaitMedia - No media yet at call time; waitForMediaStep resolves it inside the workflow
 * @property storedChatTranscript - The chat's text is already stored; the workflow loads it
 * @property storedCallTranscript - The call's transcript is already stored; the workflow loads it with its segments
 */
export const pipelineStartRequestSchema = z
  .object({
    recordId: z.string().uuid('recordId must be a valid UUID'),
    projectId: z.string().uuid('projectId must be a valid UUID'),
    orgId: z.string().uuid('orgId must be a valid UUID'),
    audioUrl: z.string().optional(),
    transcript: z.string().optional(),
    diarization: z.array(diarizationSegmentSchema).optional(),
    channel: pipelineChannelSchema,
    traceId: traceIdSchema.optional(),
    sourceLanguage: z.string().optional(),
    promptProfileId: z.string().uuid().optional(),
    /** Skip idempotency check and force new analysis (for re-analyze) */
    forceReanalyze: z.boolean().optional(),
    replay: pipelineReplaySchema.optional(),
    /**
     * Медиа ещё нет, и достать его должен воркфлоу, а не вызывающий.
     *
     * Существует потому, что этот контракт был строже собственного пайплайна:
     * штатный приём звонка из CRM создаёт инстанс, НАМЕРЕННО опустив
     * `audioUrl` (`pipeline-router.ts`, «Case 2: Late media»), а `waitForMediaStep`
     * забирает запись из источника уже внутри воркфлоу. HTTP-вход же требовал
     * `audioUrl || transcript` — и любой повторный запуск такой записи (кнопка
     * «Обработать накопленные») получал 400 на записи, которую сам продукт
     * заводит без медиа: у неё `file_uri = NULL` по построению (#400).
     *
     * Флаг, а не молчаливое снятие требования: без него пустой запрос от любого
     * вызывающего — загрузки, повторного анализа — вместо честного 400 уходил бы
     * в суточное ожидание медиа, которого никто не пришлёт. Здесь вызывающий
     * заявляет это намеренно.
     */
    awaitMedia: z.boolean().optional(),
    /**
     * Запуск инициирован человеком — кнопкой на записи, а не приёмом.
     *
     * Проектный тумблер «Автоматический анализ» выключает АВТОМАТИЧЕСКИЙ анализ;
     * явное нажатие он не отменяет. Без флага ручной запуск такой записи выглядел
     * бы как молчаливый отказ: конвейер прошёл, транскрипт есть, анализа нет, и
     * причина видна только в настройках другого экрана.
     */
    manualStart: z.boolean().optional(),
    /**
     * Текст чата уже сохранён, и поднимает его воркфлоу, а не запрос.
     *
     * Параметры инстанса воркфлоу ограничены 1 МиБ, а длинный чат из CRM —
     * больше: запуск с текстом в теле отвергался бы на `create()` навсегда, и
     * такой чат нельзя было бы запустить ничем (#1369). Воркфлоу читает
     * транскрипт из Postgres (с ролями), а без строки там — `textContent`
     * из D1, без ролей.
     */
    storedChatTranscript: z.boolean().optional(),
    /**
     * Расшифровка звонка уже сохранена — повтор анализа берёт её из Postgres
     * вместе с сегментами говорящих, без ASR и без начисления (#1443).
     *
     * Повтор слал только `transcript`, и звонок анализировался без
     * разделения на говорящих. Признак, а не `diarization` в теле: сегменты
     * уже лежат там, откуда их читает воркфлоу, а параметры инстанса
     * ограничены 1 МиБ.
     *
     * Рядом с `transcript` (и `diarization`) — допустимо, в отличие от
     * чатового признака, и это фаза выката, а не небрежность. edge-api
     * выкатывается раньше workflows-api, и старый воркфлоу этот ключ молча
     * отбрасывает: с текстом рядом повтор в окне выката работает ровно как
     * раньше, а новый воркфлоу предпочитает сохранённую копию. Текст из
     * параметров уходит отдельной задачей, после выката обоих.
     */
    storedCallTranscript: z.boolean().optional(),
    /**
     * Идентификатор ПОПЫТКИ запуска — чтобы повтор не создал второй пайплайн.
     *
     * Без него запуск не идемпотентен по последствиям: HTTP-ответ может
     * потеряться уже ПОСЛЕ того, как инстанс создан, и вызывающий, честно
     * повторивший запрос, получает второй воркфлоу на ту же запись — то есть
     * двойную обработку и двойное списание. Запуск накопленного упирался в это
     * прямо: он возвращает запись в очередь на любой сбой транспорта, а сбой
     * транспорта «ответ потерян» и «запрос не дошёл» неразличимы (#400).
     *
     * Тем же механизмом, что и `replay`: ключ становится идентификатором
     * инстанса, и коллизия разбирается `reuseCollidedWorkflow` — существующий
     * инстанс переиспользуется, а не дублируется.
     *
     * Форма — НЕ произвольная строка, и это проверяется здесь, а не выясняется
     * в проде. Cloudflare требует `^[a-zA-Z0-9_][a-zA-Z0-9-_]*$` и не длиннее
     * 100 символов (Workflows → limits). Невалидный id роняет `create()` — а
     * его отказ неотличим от коллизии, поэтому запуск уходил бы в разбор
     * коллизии, там падал на `get()` с тем же невалидным id и возвращал 500.
     * Ровно тот молчаливый отказ, ради устранения которого затевался #400,
     * только теперь на каждой записи. Первым ключом стал `backlog:<recordId>`
     * — с двоеточием, то есть невалидный; поймано ревью, до прода не дошло.
     */
    startId: z
      .string()
      .min(1)
      .max(100)
      .regex(
        /^[a-zA-Z0-9_][a-zA-Z0-9\-_]*$/,
        'Workflow instance id must match ^[a-zA-Z0-9_][a-zA-Z0-9-_]*$'
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    // Оба ключа сразу означали бы два разных идентификатора одного инстанса, и
    // выигрывал бы молча один из них.
    if (data.replay && data.startId) {
      ctx.addIssue({
        code: 'custom',
        path: ['startId'],
        message: 'Replay already carries its own deterministic id',
      });
    }
    if (data.replay) {
      if (!data.forceReanalyze) {
        ctx.addIssue({
          code: 'custom',
          path: ['forceReanalyze'],
          message: 'Replay requires forceReanalyze=true',
        });
      }
      if (
        data.audioUrl ||
        data.transcript ||
        data.diarization ||
        data.awaitMedia ||
        data.storedChatTranscript ||
        data.storedCallTranscript
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['replay'],
          message: 'Replay loads its exact transcript source server-side',
        });
      }
      return;
    }
    if (data.awaitMedia) {
      // Ждать можно только запись. У чата медиа нет вовсе: его текст либо уже
      // есть (`transcript`), либо воркфлоу дочитывает сессию из CRM — и это
      // другой путь, со своим `awaitingChatCompletion`.
      if (data.channel !== 'call') {
        ctx.addIssue({
          code: 'custom',
          path: ['awaitMedia'],
          message: 'awaitMedia applies to calls only',
        });
      }
      // Медиа либо уже есть, либо его ждут. Оба поля сразу — это два разных
      // источника истины в одном запросе, и воркфлоу молча предпочёл бы
      // `audioUrl`, сделав флаг незаметно неверным.
      if (
        data.audioUrl ||
        data.transcript ||
        data.storedChatTranscript ||
        data.storedCallTranscript
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['awaitMedia'],
          message: 'awaitMedia means the pipeline resolves media itself',
        });
      }
      return;
    }
    if (data.storedChatTranscript) {
      // Источник один — сохранённый текст. Рядом с присланным текстом или
      // аудио воркфлоу молча выбрал бы одно из двух.
      if (data.channel !== 'chat') {
        ctx.addIssue({
          code: 'custom',
          path: ['storedChatTranscript'],
          message: 'storedChatTranscript applies to chats only',
        });
      }
      if (
        data.audioUrl ||
        data.transcript ||
        data.diarization ||
        data.storedCallTranscript
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['storedChatTranscript'],
          message: 'storedChatTranscript means the pipeline loads the transcript itself',
        });
      }
      return;
    }
    if (data.storedCallTranscript) {
      // Только звонок: у чата свой признак, со своим запасным источником (D1).
      // Аудио рядом — два источника, и воркфлоу молча выбрал бы одно.
      // `transcript` рядом допустим (см. поле): запас для воркфлоу без признака
      // и для звонка, у которого строки в Postgres нет.
      if (data.channel !== 'call') {
        ctx.addIssue({
          code: 'custom',
          path: ['storedCallTranscript'],
          message: 'storedCallTranscript applies to calls only',
        });
      }
      // Чатовый признак сюда не доходит: его ветка выше отвергает этот рядом.
      if (data.audioUrl) {
        ctx.addIssue({
          code: 'custom',
          path: ['storedCallTranscript'],
          message: 'storedCallTranscript means the pipeline loads the stored transcript',
        });
      }
      // Присланный текст — запас, и пустой запас хуже никакого: воркфлоу
      // отвергает пустой `transcript` при разборе входа, то есть ответ 200
      // здесь означал бы инстанс, который сразу умер, так и не тронув запись.
      // Отказ — здесь, до инстанса.
      if (data.transcript !== undefined && !data.transcript.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['transcript'],
          message: 'transcript alongside storedCallTranscript must not be empty',
        });
      }
      return;
    }
    if (!data.audioUrl && !data.transcript) {
      ctx.addIssue({
        code: 'custom',
        path: ['audioUrl', 'transcript'],
        message: 'Either audioUrl, transcript, or awaitMedia must be provided',
      });
    }
  });

// ============================================================================
// Pipeline Start Response
// ============================================================================

/**
 * The status a started (or re-resolved) workflow instance is actually in.
 *
 * Mirrors `InstanceStatus['status']` from `@cloudflare/workers-types` — the
 * exact set `WorkflowInstance.status()` can return — because the route reports
 * that value verbatim on the replay-collision path. It is NOT always
 * `'running'`: a retry that collides on the deterministic replay ID answers
 * with whatever the existing instance is doing, which is the whole point of
 * that path (a hardcoded 'running' told the caller a dead replay was still in
 * flight).
 */
export const pipelineInstanceStatusSchema = z.enum([
  'queued',
  'running',
  'paused',
  'errored',
  'terminated',
  'complete',
  'waiting',
  'waitingForPause',
  'unknown',
]);

/**
 * Response from POST /api/v1/pipelines/start
 *
 * Confirms a workflow instance exists for this request, and says what it is
 * doing.
 *
 * @property success - Always true if request succeeded
 * @property workflowId - Cloudflare Workflow instance ID (for tracking/debugging)
 * @property status - The instance's real status, not an assumption
 * @property message - Human-readable confirmation message
 */
export const pipelineStartResponseSchema = z.object({
  success: z.boolean(),
  workflowId: z.string(),
  status: pipelineInstanceStatusSchema,
  /** True when a retry resolved the already-created deterministic replay. */
  reusedExisting: z.boolean().default(false),
  /**
   * True when that already-created replay was found DEAD (`errored`) and
   * restarted from the beginning.
   *
   * Distinct from `reusedExisting`, and always sent by the route: a replay
   * failure is terminal and the replay ID is permanent, so without a restart
   * the user's retry would collide forever with the corpse of the first
   * attempt. The caller has to be able to tell "your run is already going" from
   * "your run was dead and we started it over".
   */
  restartedExisting: z.boolean().default(false),
  message: z.string(),
});

// ============================================================================
// Type Exports
// ============================================================================

export type PipelineChannel = z.infer<typeof pipelineChannelSchema>;
export type ReplayMode = z.infer<typeof replayModeSchema>;
export type AnalysisPublication = z.infer<typeof analysisPublicationSchema>;
export type PipelineReplay = z.infer<typeof pipelineReplaySchema>;
export type DiarizationSegment = z.infer<typeof diarizationSegmentSchema>;
export type PipelineStartRequest = z.infer<typeof pipelineStartRequestSchema>;
export type PipelineInstanceStatus = z.infer<typeof pipelineInstanceStatusSchema>;
export type PipelineStartResponse = z.infer<typeof pipelineStartResponseSchema>;
